/**
 * The Card Editor's single reducer: one pure function over one card document.
 *
 * Every case ends in `normalizeCard`, so the document invariants (see
 * `normalize.ts`) are re-established after each mutation instead of being
 * re-argued per action. No React, no storage, no clock — `updatedAt` is stamped
 * by the autosave hook at write time, which is why a reducer call alone never
 * changes it.
 */

import {
  CTA_FIELD_ID,
  DESC_FIELD_ID,
  DEFAULT_CTA_CONFIGURATION,
  IMAGE_FIELD_ID,
  getDefaultFieldConfiguration,
} from '../constants';
import { BlockPlacement, FieldId, ProductCard } from '../types';
import { CardAction, PairSide } from './actions';
import {
  getFieldConfiguration,
  groupInfoRows,
  keepCtaLast,
  moveFieldAfter,
  moveFieldBefore,
  normalizeCard,
  normalizeCtaConfiguration,
} from './normalize';

const replacePlacement = (
  placements: BlockPlacement[],
  fieldId: FieldId,
  next: BlockPlacement
): BlockPlacement[] =>
  placements.map((placement) => (placement.fieldId === fieldId ? next : placement));

/** Info rows as they stand with the moved block already lifted out. */
const infoRowsWithout = (card: ProductCard, fieldId: FieldId) =>
  groupInfoRows(
    card.placements.filter(
      (placement) => placement.region === 'info' && placement.fieldId !== fieldId
    )
  );

const moveIntoInfoRow = (
  card: ProductCard,
  fieldId: FieldId,
  rowIndex: number
): ProductCard => {
  const rows = infoRowsWithout(card, fieldId);
  const target = Math.max(0, Math.min(rowIndex, rows.length));

  let fieldOrder = card.fieldOrder;
  if (target < rows.length) {
    fieldOrder = moveFieldBefore(fieldOrder, fieldId, rows[target].placements[0].fieldId);
  } else if (rows.length > 0) {
    const lastRow = rows[rows.length - 1];
    const lastFieldId = lastRow.placements[lastRow.placements.length - 1].fieldId;
    fieldOrder = moveFieldAfter(fieldOrder, fieldId, lastFieldId);
  }

  return normalizeCard({
    ...card,
    fieldOrder,
    placements: replacePlacement(card.placements, fieldId, {
      fieldId,
      region: 'info',
      order: 0,
    }),
  });
};

const pairBlocks = (
  card: ProductCard,
  fieldId: FieldId,
  peerFieldId: FieldId,
  side: PairSide
): ProductCard => {
  if (fieldId === peerFieldId) {
    return card;
  }
  const peer = card.placements.find((placement) => placement.fieldId === peerFieldId);
  if (!peer || peer.region !== 'info') {
    return card;
  }

  const rowKey = peer.rowKey ?? `row:${peerFieldId}`;
  const fieldOrder =
    side === 'left'
      ? moveFieldBefore(card.fieldOrder, fieldId, peerFieldId)
      : moveFieldAfter(card.fieldOrder, fieldId, peerFieldId);

  const placements = card.placements.map((placement) => {
    if (placement.fieldId === fieldId) {
      return { fieldId, region: 'info' as const, order: 0, rowKey };
    }
    if (placement.fieldId === peerFieldId) {
      return { ...placement, rowKey };
    }
    return placement;
  });

  return normalizeCard({ ...card, fieldOrder, placements });
};

const disableField = (card: ProductCard, fieldId: FieldId): ProductCard =>
  normalizeCard({
    ...card,
    enabledFieldIds: card.enabledFieldIds.filter((candidate) => candidate !== fieldId),
  });

export const productCardReducer = (state: ProductCard, action: CardAction): ProductCard => {
  switch (action.type) {
    case 'loadCard':
      return normalizeCard(action.card);

    case 'rename':
      return normalizeCard({ ...state, name: action.name });

    case 'setOrientation':
      return normalizeCard({ ...state, orientation: action.orientation });

    case 'setFooterEnabled':
      // Turning the footer off returns any floating block to Info (R53) — that
      // migration is one of `normalizeCard`'s invariants.
      return normalizeCard({ ...state, footerEnabled: action.enabled });

    case 'toggleField': {
      if (!action.enabled) {
        return disableField(state, action.fieldId);
      }
      const enabledFieldIds = state.enabledFieldIds.includes(action.fieldId)
        ? state.enabledFieldIds
        : [...state.enabledFieldIds, action.fieldId];
      const configuration = state.configuration[action.fieldId]
        ? state.configuration
        : { ...state.configuration, [action.fieldId]: getDefaultFieldConfiguration(action.fieldId) };
      return normalizeCard({
        ...state,
        enabledFieldIds,
        fieldOrder: keepCtaLast(
          state.fieldOrder,
          action.fieldId,
          enabledFieldIds.includes(CTA_FIELD_ID)
        ),
        configuration,
      });
    }

    case 'moveField':
      return normalizeCard({
        ...state,
        fieldOrder: moveFieldBefore(state.fieldOrder, action.fieldId, action.beforeFieldId),
      });

    case 'setDisplayLabel':
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [action.fieldId]: {
            ...getFieldConfiguration(state, action.fieldId),
            displayLabel: action.displayLabel,
          },
        },
      });

    case 'setAlignment':
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [action.fieldId]: {
            ...getFieldConfiguration(state, action.fieldId),
            alignment: action.alignment,
          },
        },
      });

    case 'setImageFit':
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [IMAGE_FIELD_ID]: {
            ...getFieldConfiguration(state, IMAGE_FIELD_ID),
            imageFit: action.imageFit,
          },
        },
      });

    case 'setCustomCss':
      // Stored as typed; `normalizeCard` caps the length and `styling/` confines it
      // on the way to the preview (R67, R68, ADR-4A).
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [action.fieldId]: {
            ...getFieldConfiguration(state, action.fieldId),
            customCss: action.customCss,
          },
        },
      });

    case 'setStrikethrough':
      // Per element (14021). `normalizeCard` writes it out as `false` when off
      // rather than dropping the key, so the stored card lists every option its
      // panel offers (operator, 2026-09-28).
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [action.fieldId]: {
            ...getFieldConfiguration(state, action.fieldId),
            strikethrough: action.strikethrough,
          },
        },
      });

    case 'setShowUom':
      // Per element since 14021 (R69) — it was one card-wide flag before that, and a
      // panel headed "List Price" changing Sale Price was the reported bug.
      // `normalizeCard` writes it out as `false` when off rather than dropping the
      // key (operator, 2026-09-28).
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [action.fieldId]: {
            ...getFieldConfiguration(state, action.fieldId),
            showUom: action.showUom,
          },
        },
      });

    case 'setDescriptionStyle':
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [DESC_FIELD_ID]: {
            ...getFieldConfiguration(state, DESC_FIELD_ID),
            descStyle: action.descStyle,
          },
        },
      });

    case 'setCtaOptions': {
      const current = getFieldConfiguration(state, CTA_FIELD_ID).cta ?? DEFAULT_CTA_CONFIGURATION;
      return normalizeCard({
        ...state,
        configuration: {
          ...state.configuration,
          [CTA_FIELD_ID]: {
            ...getFieldConfiguration(state, CTA_FIELD_ID),
            cta: normalizeCtaConfiguration({ ...current, ...action.options }),
          },
        },
      });
    }

    case 'moveBlockToInfoRow':
      return moveIntoInfoRow(state, action.fieldId, action.rowIndex);

    case 'pairBlock':
      return pairBlocks(state, action.fieldId, action.peerFieldId, action.side);

    case 'moveBlockToPhoto':
      return normalizeCard({
        ...state,
        placements: replacePlacement(state.placements, action.fieldId, {
          fieldId: action.fieldId,
          region: 'photo',
          order: 0,
          anchor: action.anchor,
        }),
      });

    case 'moveBlockToFloating':
      // The Floating region *is* the footer strip; with no footer there is
      // nowhere to pin the block, so the drop is a no-op (R53).
      if (!state.footerEnabled) {
        return state;
      }
      return normalizeCard({
        ...state,
        placements: replacePlacement(state.placements, action.fieldId, {
          fieldId: action.fieldId,
          region: 'floating',
          order: 0,
          anchor: 'sticky',
        }),
      });

    case 'deleteBlock':
      return disableField(state, action.fieldId);

    default:
      return state;
  }
};
